import React, { createContext, useEffect, useState } from 'react';
import { Engagement, EngagementLookup } from 'models/engagement';
import { useNavigate, useLocation } from 'react-router-dom';
import { getEngagement } from 'services/engagementService';
import { useAppDispatch } from 'hooks';
import { openNotification } from 'services/notificationService/notificationSlice';
import { SurveyLookup } from 'models/survey';

interface CreateSurveyContextValues {
    surveyForm: SurveyForm;
    handleSurveyFormChange: (_form: SurveyForm) => void;
    engagementToLink: Engagement | null;
    loading: boolean;
    availableSurveys: SurveyLookup[] | null;
    setAvailableSurveys: (surveys: SurveyLookup[]) => void;
    availableEngagements: EngagementLookup[] | null;
    setAvailableEngagements: (engagements: EngagementLookup[]) => void;
    isDisclaimerChecked: boolean;
    setIsDisclaimerChecked: (checked: boolean) => void;
    disclaimerError: boolean;
    setDisclaimerError: (error: boolean) => void;
}

const initialSurveyForm = {
    name: '',
};
export const CreateSurveyContext = createContext<CreateSurveyContextValues>({
    surveyForm: initialSurveyForm,
    handleSurveyFormChange: (_form: SurveyForm) => {
        //empty method
    },
    engagementToLink: null,
    loading: true,
    availableSurveys: null,
    setAvailableSurveys: (_surveys: SurveyLookup[]) => {
        //empty method
    },
    availableEngagements: null,
    setAvailableEngagements: (_engagements: EngagementLookup[]) => {
        //empty method
    },
    isDisclaimerChecked: false,
    setIsDisclaimerChecked: (_checked: boolean) => {
        throw new Error('setIsDisclaimerChecked method not implemented');
    },
    disclaimerError: false,
    setDisclaimerError: (_error: boolean) => {
        throw new Error('setDisclaimerError method not implemented');
    },
});

interface SurveyForm {
    name: string;
    structure?: unknown;
}

export const CreateSurveyContextProvider = ({ children }: { children: JSX.Element }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [surveyForm, setSurveyForm] = useState<SurveyForm>(initialSurveyForm);
    const [loading, setLoading] = useState(true);
    const [engagementToLink, setEngagementToLink] = useState<Engagement | null>(null);
    const [availableSurveys, setAvailableSurveys] = useState<SurveyLookup[] | null>(null);
    const [availableEngagements, setAvailableEngagements] = useState<EngagementLookup[] | null>(null);
    const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(false);
    const [disclaimerError, setDisclaimerError] = useState(false);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const engagementId = searchParams.get('engagementId');

    useEffect(() => {
        fetchEngagementToLink();
    }, [engagementId]);

    const handleEngagement = (engagement: Engagement) => {
        if (engagement.surveys?.length > 0) {
            dispatch(openNotification({ severity: 'error', text: `Engagement ${engagement.id} already has a survey` }));
            navigate(-1);
            return;
        }

        setEngagementToLink(engagement);
        setLoading(false);
    };

    const fetchEngagementToLink = async () => {
        if (!engagementId || isNaN(Number(engagementId))) {
            setLoading(false);
            return;
        }
        try {
            const engagement = await getEngagement(Number(engagementId));
            handleEngagement(engagement);
        } catch (error) {
            dispatch(openNotification({ severity: 'error', text: 'Error fetching the linked engagement' }));
            navigate(-1);
        }
    };

    const handleSurveyFormChange = (form: SurveyForm) => {
        setSurveyForm(form);
    };

    return (
        <CreateSurveyContext.Provider
            value={{
                surveyForm,
                handleSurveyFormChange,
                engagementToLink,
                loading,
                availableSurveys,
                setAvailableSurveys,
                setAvailableEngagements,
                availableEngagements,
                isDisclaimerChecked,
                setIsDisclaimerChecked,
                disclaimerError,
                setDisclaimerError,
            }}
        >
            {children}
        </CreateSurveyContext.Provider>
    );
};
